import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';

import { Auth } from 'src/providers/auth/auth';
import { Authenticated } from 'src/behaviors/authenticated';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Events } from 'src/providers/events/events';
import { InAppBrowserUtils } from 'src/shared/utils/in-app-browser-utils';
import { Me } from 'src/shared/providers/me';
import { RouteGuardReconfigurationService } from 'src/shared/providers/route-guard-reconfiguration.service';

import validationUtils from 'src/shared/utils/validation-utils';
import { CommonUtils } from 'src/shared/utils/common-utils';

type AuthMode = 'signin' | 'signup';
type Step = 'intro' | 'auth' | 'reset-password';

@Component({
  selector: 'app-auth-modal',
  templateUrl: './auth-modal.component.html',
  styleUrls: ['./auth-modal.component.scss'],
  standalone: false
})
export class AuthModalComponent implements OnInit {
  @Input() alertMessage = '';
  @Input() defaultTab: AuthMode = 'signup';
  @Input() dismissible = false;

  mode: AuthMode = 'signup';
  step: Step = 'intro';
  signInForm: UntypedFormGroup;
  signUpForm: UntypedFormGroup;
  resetPasswordForm: UntypedFormGroup;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  submitted = false;

  constructor(
    private auth: Auth,
    private authenticated: Authenticated,
    private customNavCtrl: CustomNavController,
    private events: Events,
    private fb: UntypedFormBuilder,
    private iabUtils: InAppBrowserUtils,
    private me: Me,
    private modalController: ModalController,
    private routeGuardReconfiguration: RouteGuardReconfigurationService,
    private utils: CommonUtils
  ) {
    this.signInForm = this.fb.group({
      email: ['', Validators.compose([Validators.required, validationUtils.validateEmail])],
      password: ['', Validators.required]
    });
    this.signUpForm = this.fb.group({
      firstName: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
      lastName: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
      email: ['', Validators.compose([Validators.required, validationUtils.validateEmail])],
      password: ['', Validators.compose([Validators.required, Validators.minLength(8)])]
    });
    this.resetPasswordForm = this.fb.group({
      email: ['', Validators.compose([Validators.required, validationUtils.validateEmail])]
    });
  }

  ngOnInit() {
    this.mode = this.defaultTab;
    if (this.alertMessage) {
      this.step = 'auth';
    }
  }

  dismiss() {
    this.modalController.dismiss();
  }

  // -- Intro step --

  goToSignUp() {
    this.step = 'auth';
    this.mode = 'signup';
    this.errorMessage = '';
    this.submitted = false;
  }

  goToSignIn() {
    this.step = 'auth';
    this.mode = 'signin';
    this.errorMessage = '';
    this.submitted = false;
  }

  // -- Auth step --

  switchMode(mode: AuthMode) {
    this.mode = mode;
    this.errorMessage = '';
    this.submitted = false;
  }

  async onSignIn() {
    this.submitted = true;
    this.errorMessage = '';
    if (!this.signInForm.valid) {
      return;
    }
    this.isLoading = true;
    try {
      await this.auth.login({
        username: this.signInForm.value.email,
        password: this.signInForm.value.password
      });
      const me = await this.me.fetchWithoutCache();
      await this.routeGuardReconfiguration.reconfigureGuardsForInvitedUser();
      try {
        const currentLanguage = await this.me.getMetadata(['external.language']);
        const language = currentLanguage?.[0]?.value || 'en';
        localStorage.setItem('language', language);
      } catch (err) {
        localStorage.setItem('language', 'en');
      }
      await this.authenticated.onAuthenticated(me);
      this.dismiss();
    } catch (err: any) {
      this.isLoading = false;
      if (err === 'invalid_grant') {
        this.errorMessage = 'The email and password you entered did not match our records. Please double-check and try again.';
        this.utils.showError(this.errorMessage);
      } else {
        this.errorMessage = err?.message || err?.error?.message || 'Sign in failed. Please try again.';
        this.utils.showError(this.errorMessage);
      }
    }
  }

  async onSignUp() {
    this.submitted = true;
    this.errorMessage = '';
    if (!this.signUpForm.valid) {
      return;
    }
    this.isLoading = true;
    try {
      const formData = this.signUpForm.value;
      await this.auth.getToken();
      await this.me.signUp({
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email,
        password: formData.password
      });
      await this.auth.login({
        username: formData.email,
        password: formData.password
      });
      const me = await this.me.fetchWithoutCache();
      await this.authenticated.onAuthenticated(me);
      this.dismiss();
    } catch (err: any) {
      this.isLoading = false;
      const msg = err?.message || err?.error?.message || '';
      if (msg.includes('already exists') || msg.includes('already been taken')) {
        this.errorMessage = 'There is already an account under this email. Please log in or use a different email.';
        this.utils.showError(this.errorMessage);
      } else {
        this.errorMessage = msg || 'Failed to create account. Please try again.';
        this.utils.showError(this.errorMessage);
      }
    }
  }

  // -- Social auth (placeholder) --

  async signInWithGoogle() {
    // TODO: Wire up Google sign-in once backend is ready
  }

  async signInWithApple() {
    // TODO: Wire up Apple sign-in once backend is ready
  }

  // -- Reset password --

  forgotPassword() {
    this.step = 'reset-password';
    this.errorMessage = '';
    this.successMessage = '';
    this.submitted = false;
    this.resetPasswordForm.reset();
  }

  goBackToSignIn() {
    this.step = 'auth';
    this.mode = 'signin';
    this.errorMessage = '';
    this.successMessage = '';
    this.submitted = false;
  }

  async onResetPassword() {
    this.submitted = true;
    this.errorMessage = '';
    this.successMessage = '';
    if (!this.resetPasswordForm.valid) {
      return;
    }
    this.isLoading = true;
    try {
      await this.me.recoverPassword(this.resetPasswordForm.value.email);
      this.isLoading = false;
      this.successMessage = 'Password reset instructions have been sent to your email.';
      setTimeout(() => {
        this.goBackToSignIn();
      }, 3000);
    } catch (err: any) {
      this.isLoading = false;
      if (err?.code === 404) {
        this.errorMessage = 'No account found with this email address.';
        this.utils.showError(this.errorMessage);
      } else {
        this.errorMessage = 'Failed to send reset email. Please try again.';
        this.utils.showError(this.errorMessage);
      }
    }
  }

  // -- Util --

  openAgreement() {
    this.iabUtils.openUrl('https://www.tidy.com/legal/service-provider/');
  }

  openUrl(url: string) {
    this.iabUtils.openUrl(url);
  }
}
